/**
 * Client-side WAF encoding utility.
 *
 * Azure Front Door WAF scores certain character sequences as potential threats
 * (Remote File Inclusion, SQL Injection) and rejects requests before they reach
 * the server. This module encodes those sequences into harmless alphanumeric
 * tokens so POST bodies pass the WAF boundary.
 *
 * The server-side counterpart (react-app/server/wafEncoder.js) applies the same
 * encoding when forwarding requests to Azure Functions. The Azure Function
 * decodes back to the original values before writing to Dataverse.
 *
 * The token map is supplied by the server via the /api/GetAppConfig endpoint
 * (WAFTokenMap setting). Fall back to built-in defaults if the server map is
 * absent — the defaults cover the standard WAF-triggering characters.
 */

/**
 * Default token map — matches the server-side defaults in WafTokenMapService.cs.
 * WAF_HTTPS and WAF_HTTP are intentionally omitted: WAF_COLON and WAF_SLASH
 * already encode the characters that trigger WAF rules (: and /), so dedicated
 * URL tokens are redundant.
 */
export const DEFAULT_WAF_TOKEN_MAP = [
    { token: 'WAF_DQUOTE', value: '"'  },
    { token: 'WAF_SLASH',  value: '/'  },
    { token: 'WAF_COMMA',  value: ','  },
    { token: 'WAF_COLON',  value: ':'  },
    { token: 'WAF_SEMI',   value: ';'  },
];

/** Escapes a literal string for use inside a RegExp constructor. */
function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

/**
 * Encodes WAF-triggering character sequences in all top-level string values of
 * an answers object before sending through Azure Front Door (WAF).
 *
 * Non-string values (arrays, numbers, booleans, null) are passed through
 * unchanged to preserve SurveyJS data types (checkbox arrays, etc.).
 *
 * @param {Object} answers  - SurveyJS data object (questionId → value)
 * @param {Array}  tokenMap - Token map to use; defaults to DEFAULT_WAF_TOKEN_MAP
 * @returns {Object} New object with encoded string values
 */
export function applyWafEncoding(answers, tokenMap = DEFAULT_WAF_TOKEN_MAP) {
    if (!answers || typeof answers !== 'object') return answers;

    return Object.fromEntries(
        Object.entries(answers).map(([key, val]) => {
            if (typeof val !== 'string') return [key, val];
            let encoded = val;
            for (const { token, value } of tokenMap) {
                encoded = encoded.replace(new RegExp(escapeRegExp(value), 'gi'), token);
            }
            return [key, encoded];
        })
    );
}